import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Switch,
  CircularProgress,
  Collapse,
  Alert,
} from '@mui/material';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import projectService from '../../utils/apiRequests/projectService';

const FORMAT_OPTIONS = [
  { value: 'docx', label: 'Word document (.docx)' },
  { value: 'markdown', label: 'Markdown (.md)' },
  { value: 'html', label: 'Web page (.html)' },
  { value: 'txt', label: 'Plain text (.txt)' },
];

const FORMAT_EXTENSIONS = { docx: 'docx', markdown: 'md', html: 'html', txt: 'txt' };

const LINE_HEIGHT_OPTIONS = [
  { value: 1, label: 'Single' },
  { value: 1.15, label: '1.15' },
  { value: 1.5, label: '1.5' },
  { value: 2, label: 'Double' },
];

const PAGE_SIZE_OPTIONS = [
  { value: 'letter', label: 'Letter (8.5" × 11")' },
  { value: 'a4', label: 'A4' },
  { value: 'a5', label: 'A5' },
  { value: 'legal', label: 'Legal' },
];

const FONT_OPTIONS = ['Times New Roman', 'Georgia', 'Garamond', 'Arial', 'Courier New'];
const FONT_SIZE_OPTIONS = [10, 11, 12, 14];

const METADATA_FIELDS = [
  { key: 'wordCount', label: 'Word count' },
  { key: 'creationDates', label: 'Creation dates' },
  { key: 'internalIds', label: 'Internal IDs' },
  { key: 'folderNames', label: 'Chapter & scene names (contents page)' },
  { key: 'characterNotes', label: 'Character notes' },
];

const DEFAULT_OPTIONS = {
  format: 'docx',
  lineHeight: 1,
  pageSize: 'letter',
  fontFamily: 'Times New Roman',
  fontSize: 12,
  blankLineBetweenParagraphs: false,
  keepIndents: true,
  chapterHeadingStyle: 'manuscript',
  dialogueSpacing: 'preserve',
  pageNumbers: true,
  runningHeader: false,
  copyright: false,
  copyrightText: '',
  includeNotes: false,
  scrubMetadata: false,
  metadata: {
    wordCount: false,
    creationDates: false,
    internalIds: false,
    folderNames: false,
    characterNotes: false,
  },
};

const ExportProjectModal = ({ open, onClose, projectId, projectTitle, onExported }) => {
  const { colors } = useThemeStyles();
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isDocx = options.format === 'docx';

  const setOption = (key, value) => setOptions((prev) => ({ ...prev, [key]: value }));
  const setMetadataOption = (key, value) =>
    setOptions((prev) => ({ ...prev, metadata: { ...prev.metadata, [key]: value } }));

  const selectSx = {
    '& .MuiInputLabel-root': { color: colors?.text?.secondary },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors?.border?.subtle },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
    '& .MuiSelect-select': { color: colors?.text?.primary },
    '& .MuiSvgIcon-root': { color: colors?.text?.secondary },
  };

  const sectionTitle = (label) => (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        color: colors?.text?.primary,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.72rem',
      }}
    >
      {label}
    </Typography>
  );

  const handleClose = () => {
    if (loading) return;
    setError('');
    onClose();
  };

  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: exportError } = await projectService.exportProjectFile(
        projectId,
        options
      );
      if (exportError || !data) {
        setError('Export failed. Please try again.');
        setLoading(false);
        return;
      }

      const extension = FORMAT_EXTENSIONS[options.format] || 'docx';
      const blob = data instanceof Blob ? data : new Blob([data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeTitle = (projectTitle || 'project')
        .replace(/[\\/:*?"<>|]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      link.href = url;
      link.download = `${safeTitle || 'project'}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      if (onExported) onExported(options.format);
      setLoading(false);
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setError('Export failed unexpectedly.');
      setLoading(false);
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Export Project"
      width={640}
      showCloseButton
      ariaLabelledby="export-project-modal-title"
      contentSx={{ gap: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          minHeight: 320,
        }}
      >
        {/* Format */}
        {sectionTitle('Format')}
        <TextField
          select
          label="Export as"
          value={options.format}
          onChange={(e) => setOption('format', e.target.value)}
          fullWidth
          size="small"
          sx={selectSx}
        >
          {FORMAT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Layout (docx only) */}
        <Collapse in={isDocx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sectionTitle('Layout')}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                select
                label="Line spacing"
                value={options.lineHeight}
                onChange={(e) => setOption('lineHeight', Number(e.target.value))}
                size="small"
                sx={selectSx}
              >
                {LINE_HEIGHT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Page size"
                value={options.pageSize}
                onChange={(e) => setOption('pageSize', e.target.value)}
                size="small"
                sx={selectSx}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Font"
                value={options.fontFamily}
                onChange={(e) => setOption('fontFamily', e.target.value)}
                size="small"
                sx={selectSx}
              >
                {FONT_OPTIONS.map((font) => (
                  <MenuItem key={font} value={font}>
                    {font}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Font size"
                value={options.fontSize}
                onChange={(e) => setOption('fontSize', Number(e.target.value))}
                size="small"
                sx={selectSx}
              >
                {FONT_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size} pt
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.blankLineBetweenParagraphs}
                    onChange={(e) => setOption('blankLineBetweenParagraphs', e.target.checked)}
                  />
                }
                label="Add space between paragraphs"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.keepIndents}
                    onChange={(e) => setOption('keepIndents', e.target.checked)}
                  />
                }
                label="Keep paragraph indents"
              />
            </FormGroup>

            {sectionTitle('Chapter headings')}
            <RadioGroup
              value={options.chapterHeadingStyle}
              onChange={(e) => setOption('chapterHeadingStyle', e.target.value)}
            >
              <FormControlLabel
                value="manuscript"
                control={<Radio size="small" />}
                label="Manuscript style — blank line before, large bold chapter title, two lines after"
              />
              <FormControlLabel
                value="simple"
                control={<Radio size="small" />}
                label="Simple heading (closest to a 1:1 copy of an imported document)"
              />
            </RadioGroup>
          </Box>
        </Collapse>

        {/* Dialogue */}
        {sectionTitle('Dialogue spacing')}
        <RadioGroup
          value={options.dialogueSpacing}
          onChange={(e) => setOption('dialogueSpacing', e.target.value)}
        >
          <FormControlLabel
            value="preserve"
            control={<Radio size="small" />}
            label="Keep as written"
          />
          <FormControlLabel
            value="compact"
            control={<Radio size="small" />}
            label={
              'Compact — remove blank lines around dialogue ("Hello." directly above She waved.)'
            }
          />
          <FormControlLabel
            value="spaced"
            control={<Radio size="small" />}
            label="Spaced — add a blank line between dialogue and narration"
          />
        </RadioGroup>

        {/* Extras */}
        <Collapse in={isDocx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sectionTitle('Page furniture')}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.pageNumbers}
                    onChange={(e) => setOption('pageNumbers', e.target.checked)}
                  />
                }
                label="Page numbers"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.runningHeader}
                    onChange={(e) => setOption('runningHeader', e.target.checked)}
                  />
                }
                label="Running header (Author / Title on every page)"
              />
            </FormGroup>
          </Box>
        </Collapse>

        {/* Content */}
        {sectionTitle('Content')}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={options.copyright}
                onChange={(e) => setOption('copyright', e.target.checked)}
              />
            }
            label="Include copyright page"
          />
          <Collapse in={options.copyright}>
            <TextField
              value={options.copyrightText}
              onChange={(e) => setOption('copyrightText', e.target.value)}
              fullWidth
              size="small"
              placeholder={`© ${new Date().getFullYear()} Author Name. All rights reserved.`}
              helperText="Leave empty for a standard copyright line"
              sx={{ ...selectSx, my: 1, input: { color: colors?.text?.primary } }}
            />
          </Collapse>
          <FormControlLabel
            control={
              <Checkbox
                checked={options.includeNotes}
                onChange={(e) => setOption('includeNotes', e.target.checked)}
              />
            }
            label="Include project notes (appended at the end)"
          />
        </FormGroup>

        {/* Metadata */}
        <Collapse in={isDocx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sectionTitle('Metadata')}
            <FormControlLabel
              control={
                <Switch
                  checked={options.scrubMetadata}
                  onChange={(e) => setOption('scrubMetadata', e.target.checked)}
                />
              }
              label="Scrub clean — no metadata page, and document properties (author, title) left blank"
            />
            <Collapse in={!options.scrubMetadata}>
              <Typography variant="body2" sx={{ color: colors?.text?.secondary, mb: 0.5 }}>
                Include on a &ldquo;Manuscript Information&rdquo; page:
              </Typography>
              <FormGroup sx={{ pl: 1 }}>
                {METADATA_FIELDS.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        size="small"
                        checked={options.metadata[key]}
                        onChange={(e) => setMetadataOption(key, e.target.checked)}
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            </Collapse>
          </Box>
        </Collapse>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2.5,
          pt: 2,
          borderTop: `1px solid ${colors?.border?.subtle}`,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            color: colors?.text?.secondary,
            borderColor: colors?.border?.subtle,
            '&:hover': {
              color: colors?.text?.primary,
              borderColor: colors?.border?.primary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? <CircularProgress size={22} /> : 'Export'}
        </Button>
      </Box>
    </ReusableModal>
  );
};

ExportProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  projectTitle: PropTypes.string,
  onExported: PropTypes.func,
};

ExportProjectModal.defaultProps = {
  projectTitle: '',
  onExported: null,
};

export default ExportProjectModal;
